import { Menu, MenuItem as MuiMenuItem } from '@mui/material';
import {
	MenuSingleButton,
	StyledMenuListButton,
	StyledMoreVert,
	TableHeaderStyled,
	Title,
} from './StylovyzeTable.styles';
import React, { MouseEvent } from 'react';

import StylovyzeSearchBar from '../StylovyzeSearchBar';
import { TableMenuItem } from './StylovyzeTable';

export interface StylovyzeTableHeaderProps {
	key: string;
	cell: React.ReactNode;
	width?: string;
	align?: 'center' | 'inherit' | 'justify' | 'left' | 'right';
	sortable?: boolean;
	maxWidth?: string;
	/**
	 *  Override the padding for a column
	 */
	padding?: string;
	/**
	 *  Override the borderLeft for a column
	 */
	borderLeft?: string;
	/**
	 *  Override the borderRight for a column
	 */
	borderRight?: string;
	/** data-cy identifier for column, overwrites auto-generated */
	dataCy?: string;
}

export interface SearchTableProps {
	/**
	 * Fired when the search is cancelled.
	 */
	onCancelSearch?: () => void;
	/**
	 * Fired when the text value changes.
	 */
	onChange?(query: string): void;
	/**
	 * Fired when the search icon is clicked.
	 */
	onRequestSearch?(): void;
	/**
	 * Sets placeholder for the embedded text field.
	 */
	placeholder?: string;
	/**
	 * The value of the text field.
	 */
	value?: string;
	/**
	 * Shows a spinner when this value is "true".
	 */
	searchInProgress?: boolean;
	dataCy?: string;
}

interface TableHeaderProps {
	title?: string;
	search?: SearchTableProps;
	menuItems?: TableMenuItem[];
	buttonStyle?: {
		variant?: 'text' | 'outlined' | 'contained';
		color?: 'inherit' | 'primary' | 'secondary';
	};
	titleArea?: React.ReactNode;
}

const StylovyzeTableHeader = ({
	title,
	search,
	menuItems,
	buttonStyle,
	titleArea,
}: TableHeaderProps): React.ReactElement => {
	const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

	const handleClick = (e: MouseEvent<HTMLElement>) => {
		setAnchorEl(e.currentTarget);
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	const menuItemClose = (onClick: TableMenuItem['onClick']) => () => {
		handleClose();
		onClick();
	};
	return (
		<TableHeaderStyled
			justifyContent="space-between"
			className="StylovyzeTable--header"
			container>
			{title && (
				<Title variant="h6" gutterBottom>
					{title}
				</Title>
			)}
			{search && (
				<StylovyzeSearchBar
					onCancelSearch={search?.onCancelSearch}
					onChange={search?.onChange}
					onRequestSearch={search?.onRequestSearch}
					placeholder={search?.placeholder}
					value={search?.value}
					style={{ width: '30%' }}
					searchInProgress={search?.searchInProgress}
					dataCy={search?.dataCy}
				/>
			)}
			{menuItems && menuItems.length === 1 && (
				<MenuSingleButton
					onClick={menuItems[0].onClick}
					variant={buttonStyle?.variant}
					color={buttonStyle?.color}
					disabled={menuItems[0].disabled}
					data-cy={menuItems[0].dataCy}>
					{menuItems[0].icon}
					{menuItems[0].text}
				</MenuSingleButton>
			)}
			{menuItems && menuItems.length > 1 && (
				<>
					<StyledMenuListButton
						aria-label="more"
						aria-controls="long-menu"
						aria-haspopup="true"
						onClick={handleClick}>
						<StyledMoreVert />
					</StyledMenuListButton>
					<Menu
						anchorEl={anchorEl}
						open={!!anchorEl}
						onClose={handleClose}>
						{menuItems.map((menuItem, i) => (
							<MuiMenuItem
								key={i}
								onClick={menuItemClose(menuItem.onClick)}
								data-cy={menuItem.dataCy}>
								{menuItem.icon}
								{menuItem.text}
							</MuiMenuItem>
						))}
					</Menu>
				</>
			)}
			{menuItems == undefined && titleArea && <>{titleArea}</>}
		</TableHeaderStyled>
	);
};

export default StylovyzeTableHeader;
